import React, { useState, useEffect } from 'react';

export default function WelcomePopup() {
  // 1. Start both states as false so nothing flickers on load
  const [showVideoPopup, setShowVideoPopup] = useState(false);
  const [showFormPopup, setShowFormPopup] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });

  // 2. Check session storage when the component mounts
  useEffect(() => {
    // Look for our specific key in the browser's session storage
    const hasSeenPopup = sessionStorage.getItem('Sai Enforcement And Security Pvt. Ltd.India_hasSeenPopup');
    
    // If the key doesn't exist, this is their first time on the site this session
    if (!hasSeenPopup) {
      setShowVideoPopup(true);
      // Immediately set the key so if they refresh, it's marked as seen
      sessionStorage.setItem('Sai Enforcement And Security Pvt. Ltd.India_hasSeenPopup', 'true');
    }
  }, []); // Empty dependency array means this runs exactly once on load

  // Handle closing the video and starting the 30-second timer
  const handleCloseVideo = () => {
    setShowVideoPopup(false);
    
    // 30 seconds = 30000 milliseconds
    setTimeout(() => {
      setShowFormPopup(true);
    }, 30000); 
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const whatsappNumber = '919702850777'; 
    const text = `Hello Sai Enforcement And Security Pvt. Ltd. India!%0A%0A*New Website Enquiry*%0A*Name:* ${formData.name}%0A*Email:* ${formData.email}%0A*Phone:* ${formData.phone}`;
    
    window.open(`https://wa.me/${whatsappNumber}?text=${text}`, '_blank');
    setShowFormPopup(false); // Close popup after submission
  };

  // Prevent background scrolling when either popup is open
  useEffect(() => {
    if (showVideoPopup || showFormPopup) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup function to ensure scrolling is restored if component unmounts
    return () => { document.body.style.overflow = 'unset'; };
  }, [showVideoPopup, showFormPopup]);

  // If neither popup is meant to show, render nothing
  if (!showVideoPopup && !showFormPopup) return null;

  return (
    <>
      {/* --- VIDEO POPUP --- */}
      {showVideoPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 transition-opacity">
          <div className="relative w-full max-w-4xl bg-black rounded-2xl shadow-2xl overflow-hidden border border-white/10">
            <button 
              onClick={handleCloseVideo}
              className="absolute top-4 right-4 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-black/50 text-white/70 hover:text-white hover:bg-black/80 backdrop-blur-md transition-all"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
            
            <iframe
              src="https://www.youtube.com/embed/m2aPqZMtktw?autoplay=1&mute=1&rel=0&modestbranding=1"
              title="Welcome video"
              className="w-full aspect-video"
              allow="accelerated-motion; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              frameBorder="0"
            />
          </div>
        </div>
      )}

      {/* --- ENQUIRY FORM POPUP --- */}
      {showFormPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 transition-opacity">
          <div className="relative w-full max-w-md bg-white rounded-[2rem] shadow-2xl overflow-hidden border border-slate-200">
            <button 
              onClick={() => setShowFormPopup(false)}
              className="absolute top-4 right-4 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-500 hover:text-slate-900 hover:bg-slate-200 transition-all"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>

            <div className="p-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-slate-900 tracking-tight">Find Your Dream Home</h3>
                <p className="text-sm text-slate-500 mt-2">Leave your details and our experts will get back to you.</p>
              </div>

              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">Full Name</label>
                  <input 
                    required 
                    type="text" 
                    name="name" 
                    value={formData.name} 
                    onChange={handleFormChange} 
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:ring-2 focus:ring-amber-400 focus:outline-none transition-all text-sm" 
                    placeholder="John Doe" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">Email Address</label>
                  <input 
                    required 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleFormChange} 
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:ring-2 focus:ring-amber-400 focus:outline-none transition-all text-sm" 
                    placeholder="john@example.com" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">Phone Number</label>
                  <input 
                    required 
                    type="tel" 
                    name="phone" 
                    value={formData.phone} 
                    onChange={handleFormChange} 
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:ring-2 focus:ring-amber-400 focus:outline-none transition-all text-sm" 
                    placeholder="+91 98765 43210" 
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="w-full mt-2 py-3.5 rounded-xl bg-gradient-to-r from-black to-slate-800 text-white font-bold tracking-wide hover:shadow-lg hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
                >
                  Send Enquiry
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}